import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { OptionType } from '@ru-digital-assets/ui-component/dist/stories/ui/ui-select/ui-select';
import TableWrapper from '../../modules/table-wrapper/table-wrapper';
import { RootState } from '../../types';
import currencyFormat from '../../utils/currencyFormat';
import getWordDecl from '../../utils/getWordDecl';
import UcpInfo from '../ucp-info/ucp-details';
import UcpDetailsTop from './ucp-details-top/ucp-details-top';
import UcpDocs from './ucp-docs/ucp-docs';
import UcpHeader from './ucp-header/ucp-header';
import UcpOntractors from './ucp-ontractors/ucp-ontractors';
import UcpSignForm from './ucp-sign-form/ucp-sign-form';

import './ucp-sign.scss';

interface UcpSignProps {
  certsOptions: { value: string; label: string }[];
  onClose: () => void;
  onSubmit: () => void;
  detailsList?: { refNumber: string; sum: string }[];
  totalSum: number;
  initiatorOrderInfo?: {
    inn: string;
    name: string;
    id?: number;
  };
  recipientOrderInfo?: {
    inn: string;
    name: string;
    requisites?: string;
    accountNumber?: string;
  };
  disabled: boolean;
  onCertSelect: (certificate: OptionType | OptionType[] | undefined) => void;
  selectedCert: OptionType | undefined;
  isAdjustment?: boolean;
  isOperation?: boolean;
  isAssets?: boolean;
  isSelling?: boolean;
  isUcpIssuing?: boolean;
  decisionDocs?: React.ReactNode;
  rightContent?: React.ReactNode;
}

const UcpSign: React.FC<UcpSignProps> = ({
  isAssets,
  isSelling,
  decisionDocs,
  isAdjustment,
  onClose,
  isOperation,
  certsOptions,
  selectedCert,
  onCertSelect,
  disabled,
  detailsList,
  onSubmit,
  totalSum,
  isUcpIssuing,
  initiatorOrderInfo,
  recipientOrderInfo,
  rightContent,
}) => {
  const accountId = useSelector((s: RootState) => s.auth.account.id);
  const getOrderString = useCallback(() => {
    const numOfRealizations = detailsList?.length;
    return `${
      accountId === initiatorOrderInfo?.id
        ? `Выпуск гибридного токена`
        : `Приобретение гибридного токена`
    }: \n${numOfRealizations} ${getWordDecl(numOfRealizations ?? 0, [
      'реaлизация',
      'реализации',
      'реализаций',
    ])} на сумму ${currencyFormat(totalSum)}`;
  }, [detailsList, totalSum]);

  const getOperationString = useCallback(() => {
    if (isAssets)
      return `Заявка на покупку цифровых финансовых активов на сумму ${currencyFormat(
        totalSum
      )}`;

    if (isSelling)
      return `Заявка на продажу цифровых финансовых активов на сумму ${currencyFormat(
        totalSum
      )}`;

    if (isAdjustment)
      return `Оплата после корректировки реализаций: ${currencyFormat(
        totalSum
      )}`;

    if (isOperation)
      return `Выпуск финансовых активов на сумму ${currencyFormat(totalSum)}`;

    return getOrderString();
  }, [isAssets, isAdjustment, isOperation, totalSum, isSelling]);

  const history = useHistory();
  return (
    <TableWrapper>
      <div className="ucp">
        <UcpHeader
          onClose={onClose}
          isUcpIssuing={isUcpIssuing}
          isSelling={isSelling}
        />
        <div className="ucp__body">
          <div className="ucp__left">
            <UcpDetailsTop
              isUcpIssuing={isUcpIssuing}
              operationString={getOperationString()}
            />
            <div className="ucp__content">
              {initiatorOrderInfo && recipientOrderInfo && (
                <UcpOntractors
                  initiatorInfo={initiatorOrderInfo}
                  recipient={recipientOrderInfo}
                />
              )}
              <UcpDocs docs={decisionDocs} />
              <UcpSignForm
                selected={selectedCert}
                onCertSelect={onCertSelect}
                onSubmit={onSubmit}
                disabled={disabled}
                backCallback={() =>
                  history.push(
                    isUcpIssuing
                      ? `/orders`
                      : isSelling
                      ? `/assets/selling`
                      : `/assets/buying`
                  )
                }
                btnText={
                  isUcpIssuing
                    ? accountId === initiatorOrderInfo?.id
                      ? 'Подписать решение'
                      : 'Подписать согласие и передать ЦФА'
                    : 'Подписать'
                }
                certsList={certsOptions}
              />
            </div>
          </div>
          {(!!detailsList?.length || rightContent) && (
            <div className="ucp__right">
              {!!detailsList?.length ? (
                <UcpInfo data={detailsList} />
              ) : (
                rightContent
              )}
            </div>
          )}
        </div>
      </div>
    </TableWrapper>
  );
};

export default UcpSign;
